/* ==========================================================================
   Edible Cup theme scripts
   Vanilla JS, no dependencies. Uses custom elements so sections keep
   working after Shopify theme editor reloads.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {}, cartType: 'drawer' };
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- Utilities ---------- */
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const fetchJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || theme.strings.cartError);
        error.data = data;
        throw error;
      }
      return data;
    });

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const sectionInner = (html, selector) => {
    const doc = parseHTML(html);
    return selector ? doc.querySelector(selector) : doc.body.firstElementChild;
  };

  const toast = (() => {
    let timer;
    return (message) => {
      const el = qs('[data-toast]');
      if (!el || !message) return;
      el.textContent = message;
      el.hidden = false;
      clearTimeout(timer);
      timer = setTimeout(() => {
        el.hidden = true;
      }, 3200);
    };
  })();

  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe, summary';

  const trapFocus = (container) => {
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const items = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  const updateCartCount = (count) => {
    const value = Number(count) || 0;
    qsa('[data-cart-count]').forEach((el) => {
      const changed = el.textContent.trim() !== String(value);
      el.textContent = value;
      el.classList.toggle('is-empty', value === 0);
      if (changed && value > 0) {
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      }
    });
    qsa('[data-cart-count-label]').forEach((el) => {
      el.textContent = el.textContent.replace(/\d+/, value);
    });
  };

  /* ---------- Drawers (menu, search, cart, filters) ---------- */
  let openDrawer = null;

  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    get isStaticOnDesktop() {
      return this.hasAttribute('data-desktop-static') && window.matchMedia('(min-width: 990px)').matches;
    }

    open(trigger) {
      if (this.isStaticOnDesktop) return;
      if (openDrawer && openDrawer !== this) openDrawer.close(false);
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      void this.offsetWidth;
      this.classList.add('is-open');
      document.body.classList.add('is-locked');
      openDrawer = this;
      qsa(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
      this.releaseFocus = trapFocus(this);
      const focusTarget = qs('[autofocus], input[type="search"]', this) || qs('.drawer__close', this);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close(restoreFocus = true) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      if (openDrawer === this) openDrawer = null;
      if (!openDrawer) document.body.classList.remove('is-locked');
      qsa(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (this.releaseFocus) this.releaseFocus();
      if (!this.hasAttribute('data-desktop-static')) {
        setTimeout(() => {
          if (!this.classList.contains('is-open')) this.hidden = true;
        }, 420);
      }
      if (restoreFocus && this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    if (drawer.isStaticOnDesktop) return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* ---------- Header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.setHeight();
      this.lastScroll = 0;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.setHeight());
        this.resizeObserver.observe(this);
      }
      this.setupDropdowns();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    setHeight() {
      document.documentElement.style.setProperty('--header-height', `${Math.round(this.offsetHeight)}px`);
    }

    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 8);
      this.lastScroll = y;
    }

    setupDropdowns() {
      const dropdowns = qsa('[data-hover-dropdown]', this);
      const canHover = window.matchMedia('(hover: hover)').matches;
      dropdowns.forEach((details) => {
        if (canHover) {
          let closeTimer;
          details.addEventListener('mouseenter', () => {
            clearTimeout(closeTimer);
            dropdowns.forEach((other) => other !== details && other.removeAttribute('open'));
            details.setAttribute('open', '');
          });
          details.addEventListener('mouseleave', () => {
            closeTimer = setTimeout(() => details.removeAttribute('open'), 180);
          });
        }
        details.addEventListener('toggle', () => {
          if (details.open) dropdowns.forEach((other) => other !== details && other.removeAttribute('open'));
        });
        details.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && details.open) {
            details.removeAttribute('open');
            qs('summary', details).focus();
          }
        });
      });
      document.addEventListener('click', (event) => {
        dropdowns.forEach((details) => {
          if (details.open && !details.contains(event.target)) details.removeAttribute('open');
        });
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.messages = qsa('.announcement-bar__message', this);
      this.index = 0;
      if (this.messages.length < 2) return;
      this.interval = Number(this.dataset.interval) || 5000;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.stop();
      this.timer = setInterval(() => this.show(this.index + 1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
    }

    show(index) {
      this.messages[this.index].classList.remove('is-active');
      this.index = (index + this.messages.length) % this.messages.length;
      this.messages[this.index].classList.add('is-active');
    }

    select(element) {
      const i = this.messages.indexOf(element);
      if (i > -1) {
        this.stop();
        this.show(i);
      }
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || this.dataset.enabled !== 'true' || !theme.routes.predictiveSearch) return;
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    async search() {
      const term = this.input.value.trim();
      if (!term) {
        this.close();
        return;
      }
      if (this.controller) this.controller.abort();
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
        section_id: 'predictive-search',
      });
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        const content = sectionInner(html, '[data-predictive-search-results]');
        const markup = content ? content.outerHTML : '';
        this.cache.set(term, markup);
        this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.input.setAttribute('aria-expanded', markup ? 'true' : 'false');
      this.activeIndex = -1;
    }

    close() {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      const options = qsa('[role="option"]', this.results);
      if (!options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + direction + options.length) % options.length;
        options.forEach((option, i) => option.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false'));
        this.input.setAttribute('aria-activedescendant', options[this.activeIndex].id);
      } else if (event.key === 'Enter' && this.activeIndex > -1) {
        const link = qs('a', options[this.activeIndex]);
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[name]');
        if (!button) return;
        event.preventDefault();
        const step = Number(this.input.step) || 1;
        const min = this.input.min !== '' ? Number(this.input.min) : 0;
        const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
        const current = Number(this.input.value) || 0;
        const next = button.name === 'plus' ? current + step : current - step;
        const clamped = Math.min(Math.max(next, min), max);
        if (clamped === current) return;
        this.input.value = clamped;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart ---------- */
  const Cart = {
    drawer() {
      return document.getElementById('CartDrawer');
    },

    sectionsToRender() {
      const sections = [];
      if (this.drawer()) sections.push('cart-drawer');
      const page = qs('cart-items[data-cart-context="page"]');
      if (page && page.dataset.sectionId) sections.push(page.dataset.sectionId);
      return sections;
    },

    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        if (id === 'cart-drawer') {
          const fresh = sectionInner(html, '#CartDrawerInner');
          const current = document.getElementById('CartDrawerInner');
          if (fresh && current) current.replaceWith(fresh);
          if (fresh) updateCartCount(fresh.dataset.cartCountValue);
        } else {
          const fresh = sectionInner(html, 'cart-items[data-cart-context="page"]');
          const current = qs('cart-items[data-cart-context="page"]');
          if (fresh && current) current.replaceWith(fresh);
          if (fresh) updateCartCount(fresh.dataset.cartCountValue);
        }
      });
    },

    async refresh() {
      const ids = this.sectionsToRender();
      if (!ids.length) return;
      const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      if (response.ok) this.render(await response.json());
    },

    openDrawer(trigger) {
      const drawer = this.drawer();
      if (drawer) drawer.open(trigger);
    },
  };

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.context = this.dataset.cartContext;
      this.onQuantityChange = debounce((input) => this.updateLine(input.dataset.line, input.value, input), 350);
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (input.matches('[data-cart-quantity]')) this.onQuantityChange(input);
        if (input.matches('[data-cart-note]')) this.saveNote(input.value);
      });
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-cart-note]')) this.debouncedNote(event.target.value);
      });
      this.debouncedNote = debounce((value) => this.saveNote(value), 600);
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) {
          event.preventDefault();
          this.updateLine(remove.dataset.cartRemove, 0, remove);
        }
      });
    }

    async updateLine(line, quantity, source) {
      const item = source.closest('.cart-item');
      if (item) item.classList.add('is-loading');
      try {
        const data = await fetchJSON(theme.routes.cartChange, {
          line: Number(line),
          quantity: Number(quantity),
          sections: Cart.sectionsToRender(),
          sections_url: window.location.pathname,
        });
        Cart.render(data.sections);
        updateCartCount(data.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (error) {
        if (item) {
          item.classList.remove('is-loading');
          const message = qs('[data-line-error]', item);
          if (message) {
            message.textContent = error.message || theme.strings.quantityError;
            message.hidden = false;
          }
        }
        await Cart.refresh().catch(() => {});
      }
    }

    saveNote(note) {
      fetchJSON(theme.routes.cartUpdate, { note }).catch(() => {});
    }
  }
  customElements.define('cart-items', CartItems);

  /* ---------- Product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      this.submitButton = qs('[type="submit"]', this.form);
      this.errorEl = qs('[data-form-error]', this);
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      const onCartPage = Boolean(qs('cart-items[data-cart-context="page"]'));
      if (theme.cartType !== 'drawer' && !onCartPage) return;
      event.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.disabled) return;

      this.setLoading(true);
      this.showError('');

      const formData = new FormData(this.form);
      const sections = Cart.sectionsToRender();
      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);

        Cart.render(data.sections);
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
        if (onCartPage) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          Cart.openDrawer(this.submitButton);
        }
      } catch (error) {
        this.showError(error.message);
        if (!this.errorEl) toast(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.submitButton.classList.toggle('is-loading', isLoading);
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (isLoading) this.submitButton.setAttribute('aria-disabled', 'true');
      else this.submitButton.removeAttribute('aria-disabled');
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Product page ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = qs('[data-product-json]', this);
      if (!json) return;
      try {
        this.product = JSON.parse(json.textContent);
      } catch (error) {
        return;
      }
      this.variantPicker = qs('variant-picker', this);
      this.planPicker = qs('selling-plan-picker', this);
      this.idInput = qs('[data-variant-id-input]', this);
      this.planInput = qs('[data-selling-plan-input]', this);
      this.addButton = qs('[data-add-button]', this);
      this.addLabel = qs('[data-add-label]', this);
      this.priceEl = qs(`#Price-${this.dataset.sectionId} [data-price]`, this);
      this.unitTemplate = this.priceEl ? qs('[data-unit-price]', this.priceEl) : null;
      this.gallery = qs('media-gallery', this);
      this.sticky = qs('sticky-atc', this);

      const currentId = this.idInput ? Number(this.idInput.value) : null;
      this.variant = this.product.variants.find((v) => v.id === currentId) || this.product.variants[0];
      this.planId = this.planInput && !this.planInput.disabled ? this.planInput.value : null;

      this.addEventListener('change', (event) => {
        if (event.target.closest('variant-picker')) this.onOptionChange();
        if (event.target.closest('selling-plan-picker')) this.onPlanChange();
      });

      this.markAvailability();
      this.updatePlanPrices();
    }

    selectedOptions() {
      return qsa('[data-option-index]', this.variantPicker).map((fieldset) => {
        const checked = qs('input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      this.variant = this.product.variants.find((variant) => variant.options.every((value, i) => value === options[i])) || null;

      qsa('[data-option-index]', this.variantPicker).forEach((fieldset, i) => {
        const label = qs('[data-selected-value]', fieldset);
        if (label) label.textContent = options[i] ? `: ${options[i]}` : '';
      });

      this.markAvailability();
      this.updateVariant();
    }

    onPlanChange() {
      const subscribe = qs('[data-purchase-option][value="subscribe"]', this.planPicker);
      const select = qs('[data-plan-select]', this.planPicker);
      const isSubscribe = this.product.requiresSellingPlan || (subscribe && subscribe.checked);
      this.planId = isSubscribe && select ? select.value : null;
      if (this.planInput) {
        this.planInput.value = this.planId || '';
        this.planInput.disabled = !this.planId;
      }
      this.updatePrice();
    }

    markAvailability() {
      if (!this.variantPicker) return;
      const selected = this.selectedOptions();
      qsa('[data-option-index]', this.variantPicker).forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        qsa('input', fieldset).forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const match = this.product.variants.find((variant) => variant.available && variant.options.every((value, i) => value === candidate[i]));
          input.classList.toggle('is-unavailable', !match);
        });
      });
    }

    updateVariant() {
      const variant = this.variant;

      if (this.idInput) {
        this.idInput.disabled = !variant;
        if (variant) this.idInput.value = variant.id;
      }

      if (this.addButton) {
        const available = Boolean(variant && variant.available);
        this.addButton.disabled = !available;
        const text = !variant ? theme.strings.unavailable : available ? theme.strings.addToCart : theme.strings.soldOut;
        if (this.addLabel) this.addLabel.textContent = text;
        if (this.sticky) this.sticky.update(variant, text, available);
      }

      if (variant && this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (variant && variant.featuredMediaId && this.gallery) this.gallery.showMedia(variant.featuredMediaId);

      this.updatePlanPrices();
      this.updatePrice();
    }

    currentPricing() {
      const variant = this.variant;
      if (!variant) return null;
      if (this.planId && variant.plans && variant.plans[this.planId]) {
        const plan = variant.plans[this.planId];
        return {
          price: plan.priceFormatted,
          compare: plan.price < variant.price ? variant.priceFormatted : variant.compareAtPrice > plan.price ? variant.compareFormatted : null,
          unit: variant.unitLabel,
        };
      }
      return {
        price: variant.priceFormatted,
        compare: variant.compareAtPrice > variant.price ? variant.compareFormatted : null,
        unit: variant.unitLabel,
      };
    }

    updatePrice() {
      if (!this.priceEl) return;
      const pricing = this.currentPricing();
      if (!pricing) {
        this.priceEl.classList.add('price--sold-out');
        return;
      }
      const s = theme.strings;
      let html = '';
      if (pricing.compare) {
        html += `<span class="visually-hidden">${s.salePrice}</span><span class="price__current">${pricing.price}</span>`;
        html += `<span class="visually-hidden">${s.regularPrice}</span><s class="price__compare">${pricing.compare}</s>`;
      } else {
        html += `<span class="visually-hidden">${s.regularPrice}</span><span class="price__current">${pricing.price}</span>`;
      }
      if (pricing.unit) html += `<small class="price__unit" data-unit-price>${pricing.unit}</small>`;
      this.priceEl.innerHTML = html;
      this.priceEl.classList.toggle('price--on-sale', Boolean(pricing.compare));
      this.priceEl.classList.toggle('price--sold-out', !(this.variant && this.variant.available));
      if (this.sticky) this.sticky.setPrice(pricing.price);
    }

    updatePlanPrices() {
      if (!this.planPicker || !this.variant) return;
      const once = qs('[data-once-price]', this.planPicker);
      const subscribe = qs('[data-subscribe-price]', this.planPicker);
      const select = qs('[data-plan-select]', this.planPicker);
      if (once) once.innerHTML = this.variant.priceFormatted;
      if (subscribe && select && this.variant.plans[select.value]) {
        subscribe.innerHTML = this.variant.plans[select.value].priceFormatted;
      }
    }
  }
  customElements.define('product-info', ProductInfo);

  class VariantPicker extends HTMLElement {}
  customElements.define('variant-picker', VariantPicker);

  class SellingPlanPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-plan-select]')) {
          const subscribe = qs('[data-purchase-option][value="subscribe"]', this);
          if (subscribe && !subscribe.checked) {
            subscribe.checked = true;
          }
          const info = this.closest('product-info');
          if (info && info.updatePlanPrices) info.updatePlanPrices();
        }
      });
    }
  }
  customElements.define('selling-plan-picker', SellingPlanPicker);

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slider = qs('[data-gallery-slider]', this);
      this.slides = qsa('.product-gallery__slide', this);
      this.thumbs = qsa('[data-thumb-for]', this);
      this.counter = qs('[data-gallery-current]', this);
      if (!this.slider || this.slides.length < 2) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumbFor));
      });
      const prev = qs('[data-gallery-prev]', this);
      const next = qs('[data-gallery-next]', this);
      if (prev) prev.addEventListener('click', () => this.go(-1));
      if (next) next.addEventListener('click', () => this.go(1));

      this.slider.addEventListener('scroll', debounce(() => this.syncFromScroll(), 60), { passive: true });
      this.slider.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.go(1);
        if (event.key === 'ArrowLeft') this.go(-1);
      });

      const active = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (active > 0) {
        requestAnimationFrame(() => {
          this.slider.scrollTo({ left: this.slides[active].offsetLeft, behavior: 'instant' });
        });
      }
    }

    get index() {
      return Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    }

    go(direction) {
      const target = (this.index + direction + this.slides.length) % this.slides.length;
      this.showMedia(this.slides[target].dataset.mediaId);
    }

    showMedia(mediaId) {
      const slide = this.slides.find((item) => item.dataset.mediaId === String(mediaId));
      if (!slide) return;
      this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      this.setActive(slide);
    }

    syncFromScroll() {
      const width = this.slider.clientWidth || 1;
      const index = Math.round(this.slider.scrollLeft / width);
      if (this.slides[index]) this.setActive(this.slides[index]);
    }

    setActive(slide) {
      this.slides.forEach((item) => item.classList.toggle('is-active', item === slide));
      const id = slide.dataset.mediaId;
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.thumbFor === id;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = this.slides.indexOf(slide) + 1;
      qsa('video', this).forEach((video) => {
        if (!slide.contains(video)) video.pause();
      });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const info = this.closest('product-info');
      this.target = info ? qs('[data-buy-buttons]', info) : null;
      this.button = qs('[data-sticky-submit]', this);
      this.priceEl = qs('[data-sticky-price]', this);
      if (!this.target || !('IntersectionObserver' in window)) return;

      this.hidden = false;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const pastButtons = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', pastButtons);
        },
        { threshold: 0 }
      );
      this.observer.observe(this.target);

      this.button.addEventListener('click', () => {
        const mainButton = qs('[data-add-button]', this.target);
        if (mainButton && !mainButton.disabled) mainButton.click();
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    update(variant, text, available) {
      if (!this.button) return;
      this.button.textContent = text;
      this.button.disabled = !available;
    }

    setPrice(price) {
      if (this.priceEl) this.priceEl.innerHTML = price;
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      const load = () => {
        this.loaded = true;
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((html) => {
            const fresh = sectionInner(html, 'product-recommendations');
            if (fresh && fresh.innerHTML.trim()) {
              this.innerHTML = fresh.innerHTML;
              initReveal(this);
            } else {
              this.closest('.shopify-section')?.classList.add('is-empty');
              this.hidden = true;
            }
          })
          .catch(() => {});
      };
      if (!('IntersectionObserver' in window)) return load();
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            load();
          }
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  class ShareButton extends HTMLElement {
    connectedCallback() {
      const trigger = qs('[data-share-trigger]', this);
      const copied = qs('[data-share-copied]', this);
      if (!trigger) return;
      trigger.addEventListener('click', async () => {
        const data = { title: this.dataset.title, url: this.dataset.url };
        if (navigator.share) {
          try {
            await navigator.share(data);
          } catch (error) {
            /* user cancelled */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(data.url);
          if (copied) {
            copied.hidden = false;
            setTimeout(() => (copied.hidden = true), 2500);
          }
        } catch (error) {
          window.prompt('', data.url);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ---------- Collection filters ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = qs('[data-facet-form]', this);
      if (!this.form) return;
      this.section = this.closest('[data-section-id]');
      this.debouncedUpdate = debounce(() => this.update(), 450);

      this.form.addEventListener('change', (event) => {
        if (!event.target.matches('[data-facet-input]')) return;
        const isSort = event.target.name === 'sort_by';
        const desktop = window.matchMedia('(min-width: 990px)').matches;
        if (isSort || desktop) this.debouncedUpdate();
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.update();
      });
    }

    buildQuery() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      return params.toString();
    }

    async update() {
      const query = this.buildQuery();
      const url = `${this.form.action}${query ? `?${query}` : ''}`;
      const sectionId = this.section ? this.section.dataset.sectionId : null;
      const drawer = qs('drawer-panel', this);
      if (!sectionId) {
        window.location.href = url;
        return;
      }
      const results = qs('.collection__results', this);
      if (results) results.classList.add('is-loading');
      try {
        const response = await fetch(`${url}${query ? '&' : '?'}section_id=${sectionId}`);
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        const fresh = sectionInner(html, `[data-section-id="${sectionId}"]`);
        if (!fresh) throw new Error('missing');
        if (drawer && drawer.classList.contains('is-open')) drawer.close(false);
        this.section.replaceWith(fresh);
        window.history.pushState({ facets: true }, '', url);
        initReveal(fresh);
      } catch (error) {
        window.location.href = url;
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.facets) window.location.reload();
  });

  /* ---------- Scroll reveal ---------- */
  const revealObserver =
    'IntersectionObserver' in window && !designMode
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-revealed');
                revealObserver.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;

  function initReveal(root = document) {
    qsa('[data-reveal]:not(.is-revealed)', root).forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-revealed');
    });
  }

  /* ---------- Forms ---------- */
  function initLocalization(root = document) {
    qsa('[data-localization-form] select', root).forEach((select) => {
      select.addEventListener('change', () => select.form.submit());
    });
  }

  function initAddressForms(root = document) {
    qsa('[data-address-country]', root).forEach((countrySelect) => {
      const form = countrySelect.closest('form');
      const provinceSelect = qs('[data-address-province]', form);
      const provinceField = provinceSelect ? provinceSelect.closest('.field') : null;
      const initial = countrySelect.dataset.addressCountry;
      if (initial) countrySelect.value = initial;

      const populate = (selectProvince) => {
        if (!provinceSelect) return;
        const option = countrySelect.options[countrySelect.selectedIndex];
        let provinces = [];
        try {
          provinces = JSON.parse((option && option.dataset.provinces) || '[]');
        } catch (error) {
          provinces = [];
        }
        provinceSelect.innerHTML = '';
        provinces.forEach(([value, label]) => provinceSelect.add(new Option(label, value)));
        if (provinceField) provinceField.hidden = provinces.length === 0;
        if (selectProvince) provinceSelect.value = selectProvince;
      };

      populate(provinceSelect ? provinceSelect.dataset.addressProvince : '');
      countrySelect.addEventListener('change', () => populate(''));
    });

    qsa('form[data-confirm]', root).forEach((form) => {
      form.addEventListener('submit', (event) => {
        if (!window.confirm(form.dataset.confirm)) event.preventDefault();
      });
    });
  }

  /* ---------- Init ---------- */
  function init(root = document) {
    initReveal(root);
    initLocalization(root);
    initAddressForms(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* ---------- Theme editor support ---------- */
  if (designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      init(event.target);
      qsa('[data-reveal]', event.target).forEach((el) => el.classList.add('is-revealed'));
    });
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.querySelector('#CartDrawer')) Cart.openDrawer();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      const drawer = event.target.querySelector('#CartDrawer');
      if (drawer) drawer.close(false);
    });
    document.addEventListener('shopify:block:select', (event) => {
      const rotator = event.target.closest('announcement-rotator');
      if (rotator) rotator.select(event.target);
    });
    document.addEventListener('shopify:block:deselect', (event) => {
      const rotator = event.target.closest('announcement-rotator');
      if (rotator) rotator.start();
    });
    qsa('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
  }

  window.EdibleCup = { Cart, updateCartCount, toast };
})();
